import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { Address } from "@models";
import client from "@services/api";
import { buildPaginatedQuery } from "@utils/reactQuery";

import type {
  CreateAddressPayload,
  UpdateAddressPayload,
} from "./addressFormHelpers";

export type {
  CreateAddressPayload,
  UpdateAddressPayload,
} from "./addressFormHelpers";

const API_RESOURCE_PATH = "addresses";
export const addressesKeyFactory = createQueryKeys("addresses", {
  list: (params) => ({
    queryKey: [params],
    queryFn: () =>
      client
        .get<Address[]>(API_RESOURCE_PATH, { params })
        .then((res) => res.data),
  }),
  paginated: (params) => ({
    queryKey: [params],
    queryFn: () => client.get<Address[]>(API_RESOURCE_PATH, { params }),
  }),
});

export const useAddressesQuery = (filter: Record<string, any>) => {
  return useQuery({
    ...addressesKeyFactory.list({ filter }),
    placeholderData: keepPreviousData,
    staleTime: 20_000,
  });
};

export const usePaginatedAddressQuery = buildPaginatedQuery<Address>(
  addressesKeyFactory.paginated,
  { placeholderData: keepPreviousData, staleTime: 20_000 }
);

export const useCreateAddressMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAddressPayload) =>
      client
        .post<Address>(`${API_RESOURCE_PATH}`, {
          ...data,
          __type: "address",
          relationshipNames: ["state", "subState"],
        })
        .then((res) => res.data),
    onSuccess: (_a) => {
      queryClient.invalidateQueries({
        queryKey: addressesKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({
        queryKey: addressesKeyFactory.list._def,
      });
    },
  });
};
export const useUpdateAddressMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateAddressPayload) =>
      client
        .update<Address>(`${API_RESOURCE_PATH}/${id}`, {
          ...data,
          __type: "address",
        })
        .then((res) => res.data),
    onSuccess: (_a) => {
      queryClient.invalidateQueries({
        queryKey: addressesKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({
        queryKey: addressesKeyFactory.list._def,
      });
    },
  });
};

type UpdateUserAttnMutation = {
  id: string;
  userAttn: string;
};

/** Per-user attention override on an address (Profile → Edit Attention Lines). */
export const useUpdateUserAttnMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userAttn }: UpdateUserAttnMutation) =>
      client
        .patch<Address>(
          `${API_RESOURCE_PATH}/${id}/current-user-attn`,
          { "user-attn": userAttn },
          { serializeBody: false }
        )
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: addressesKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({
        queryKey: addressesKeyFactory.list._def,
      });
    },
  });
};
